import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { CheckCircle2, XCircle } from 'lucide-react';

// Fungsi bantu untuk hitung jarak antar koordinat (Haversine Formula)
function getDistance(lat1, lon1, lat2, lon2) {
  const R = 6371; // radius bumi km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
    Math.cos((lat2 * Math.PI) / 180) *
    Math.sin(dLon / 2) *
    Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c * 1000; // meter
}

function LokasiStatus() {
  const [lokasiAbsen, setLokasiAbsen] = useState([]);
  const [lokasiTerdeteksi, setLokasiTerdeteksi] = useState(null);

  useEffect(() => {
    try {
      const lokasiAbsenData = JSON.parse(localStorage.getItem('data_lokasi_absen')) || [];
      const lokasiTerdeteksiData = JSON.parse(localStorage.getItem('lokasi_terdeteksi')) || null;
      setLokasiAbsen(lokasiAbsenData);
      setLokasiTerdeteksi(lokasiTerdeteksiData);
    } catch (error) {
      console.error('Gagal memuat data lokasi dari localStorage:', error);
    }
  }, []);

  // Fungsi klik untuk validasi lokasi berdasarkan nama kantor
  const handleValidasi = (lokasi) => {
    Swal.fire({
      title: 'Memproses validasi...',
      text: `Sedang mengecek lokasi ${lokasi.nama}`,
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    if (!navigator.geolocation) {
      Swal.fire('Error', 'Browser tidak mendukung geolokasi.', 'error');
      return;
    }

    // Daftar koordinat kantor berdasarkan nama dari data_lokasi_absen
    const lokasiKantor = [
      { name: 'Cilacap', latitude: -7.703791784344155, longitude: 109.02706575208764 },
      { name: 'Tritih', latitude: -7.67349182341826, longitude: 109.0158536734119 },
    ];

    // Ambil koordinat berdasarkan nama kantor yang diklik
    const kantorTujuan = lokasiKantor.find((k) => k.name.toLowerCase() === lokasi.nama.toLowerCase());
    if (!kantorTujuan) {
      Swal.fire({
        icon: 'warning',
        title: 'Peringatan',
        text: 'Anda belum berada di JNE ' + lokasi.nama,
        confirmButtonText: 'OK', // Mengatur teks tombol menjadi "OK"
        showCancelButton: false, // Menghilangkan tombol "Cancel"
      });
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;

        // Cek jarak terhadap koordinat kantor yang sesuai
        const jarak = getDistance(latitude, longitude, kantorTujuan.latitude, kantorTujuan.longitude);
        const isWithinRange = jarak <= 100; // Radius 100 meter

        if (isWithinRange) {
          const lokasiBaru = {
            nama_kantor: kantorTujuan.name,
            latitude,
            longitude,
            waktu: new Date().toLocaleTimeString(),
            tanggal: new Date().toISOString().split('T')[0],
          };

          localStorage.setItem('lokasi_terdeteksi', JSON.stringify(lokasiBaru));
          setLokasiTerdeteksi(lokasiBaru);

          Swal.fire('Berhasil', `Lokasi berhasil divalidasi di ${kantorTujuan.name}`, 'success');
        } else {
          Swal.fire('Gagal', `Anda tidak berada di JNE ${kantorTujuan.name} (jarak: ${jarak.toFixed(2)} meter), tapi tetap bisa absen, silahkan ke menu Absen.`, 'error');
        }
      },
      (err) => {
        console.error(err);
        Swal.fire('Error', 'Gagal mendapatkan lokasi perangkat.', 'error');
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  return (
    <div
      style={{
        backgroundColor: 'transparent',
        borderRadius: 0,
        padding: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        overflowX: 'auto',
        gap: 10,
        scrollbarWidth: 'none',
        width: '100%',
      }}
    >
      {lokasiAbsen.length > 0 ? (
        lokasiAbsen.map((lokasi) => {
          const isValid =
            lokasiTerdeteksi &&
            lokasiTerdeteksi.nama_kantor?.toLowerCase().includes(lokasi.nama.toLowerCase());

          return (
            <div
              key={lokasi.id}
              onClick={() => !isValid && handleValidasi(lokasi)}
              style={{
                backgroundColor: isValid ? '#f0fdf4' : '#ffffff',
                border: `1px solid ${isValid ? '#86efac' : '#cbd5e1'}`,
                borderRadius: 14,
                padding: '0 14px',
                minWidth: 130,
                height: 42,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                boxShadow: isValid ? '0 2px 6px rgba(22, 163, 74, 0.08)' : '0 1px 3px rgba(0,0,0,0.04)',
                cursor: isValid ? 'default' : 'pointer',
                transition: 'all 0.2s ease',
                gap: 10,
                flexShrink: 0,
              }}
            >
              {/* NAMA LOKASI */}
              <span
                style={{
                  fontWeight: 600,
                  fontSize: 14,
                  color: isValid ? '#15803d' : '#334155',
                  letterSpacing: '-0.2px',
                }}
              >
                {lokasi.nama}
              </span>

              {/* IKON VALID / INVALID */}
              {isValid ? (
                <CheckCircle2 size={18} color="#16a34a" />
              ) : (
                <XCircle
                  size={18}
                  color="#ef4444"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleValidasi(lokasi);
                  }}
                  style={{ cursor: 'pointer' }}
                />
              )}
            </div>
          );
        })
      ) : (
        <p style={{ color: '#888', fontSize: 13, margin: 0, textAlign: 'center', width: '100%' }}>
          Klik Icon Update di pojok kanan atas untuk menampilkan Menu
        </p>
      )}
    </div>
  );
}

export default LokasiStatus;